import * as React from 'react';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { AdminApi, QueueReport, QueueReports } from '../../../../../api/orcid';
import { AuthContext } from '../../../../../Auth/components/AuthProvider';
import { getAuthMiddleware } from '../../../../../Auth/utils/GetAuthMiddleware';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';
import { getMandantSettingsByUsername } from '../../../../../util/mandant-settings-utils';
import { ToastContext, ToastContextType } from '../../../../../util/providers/ToastProvider';

export interface OrcidReportsProps {
  userName: string;
}

export function OrcidReports(props: OrcidReportsProps): ReactNode {
  const institution: string = getMandantSettingsByUsername(props.userName).institution;
  const intl: IntlShape = useIntl();
  const [loading, setLoading] = useState<boolean>(false);
  const [reports, setReports] = useState<QueueReport[]>([]);
  const [globalFilterValue, setGlobalFilterValue] = useState<string>('');
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
  });
  const toast: ToastContextType = useContext(ToastContext);
  const {actions} = useContext(AuthContext);
  const api: AdminApi = new AdminApi().withMiddleware(getAuthMiddleware(actions));

  useEffect((): void => {
    void loadData();
  }, []);

  const loadData = (): void => {
    setLoading(true);
    void api
      .getReports({
        institution,
      })
      .then((queueReports: QueueReports): void => {
        if (queueReports) {
          setReports(queueReports.queueReportList ?? []);
        }
        setLoading(false);
      })
      .catch((e): void => {
        setReports([]);
        setLoading(false);
        toast.error(e, 'message.loading.failed');
      });
  };

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    const _filters = {...filters};

    (_filters.global as any).value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const sortTimestamp = (event: {data: QueueReport[]; order?: 1 | 0 | -1 | null}): void => {
    const sortOrder = event.order ?? 1;

    event.data.sort((firstReport: QueueReport, secondReport: QueueReport): number => {
      const firstTimestamp = Date.parse(firstReport.queueJob?.timestamp ?? '');
      const secondTimestamp = Date.parse(secondReport.queueJob?.timestamp ?? '');

      return (firstTimestamp - secondTimestamp) * sortOrder;
    });
  };

  const header: ReactNode = (
    <div className="flex justify-content-end">
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search"/>
        <InputText value={globalFilterValue} onChange={onGlobalFilterChange}
                   placeholder={intl.formatMessage({id: 'search.placeholder'})}/>
      </IconField>
    </div>
  );

  return (
    <section className="orcid-reports">
      <h2>{intl.formatMessage({id: 'orcidAdminArea.tab.reports'})}</h2>
      <DataTable
        filters={filters}
        value={reports}
        stripedRows={true}
        showGridlines={true}
        loading={loading}
        dataKey="queueJob.identifier"
        paginator={true}
        rowsPerPageOptions={[10, 25, 50]}
        rows={10}
        header={header}
        globalFilterFields={['queueJob.identifier', 'queueJob.timestamp', 'errorMessage']}
      >
        <Column field="queueJob.identifier" sortable={true}
                header={intl.formatMessage({id: 'orcidAdminArea.header.job.identifier'})}></Column>
        <Column field="queueJob.timestamp" sortable={true} sortFunction={sortTimestamp}
                header={intl.formatMessage({id: 'orcidAdminArea.header.job.timestamp'})}></Column>
        <Column field="status"
                sortable={true}
                header={intl.formatMessage({id: 'orcidAdminArea.header.status'})}
                body={(report): ReactNode => {
                  let severity: 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast' | null | undefined;
                  switch (report.status) {
                    case 'ok':
                      severity = 'success';
                      break;
                    case 'failed':
                      severity = 'danger';
                      break;
                    default:
                      severity = 'warning';
                      break;
                  }
                  return <Tag severity={severity} value={report.status} key={report.identifier}/>;
                }}
        ></Column>
        <Column field="errorMessage" sortable={true}
                header={intl.formatMessage({id: 'orcidAdminArea.header.errorMessage'})}></Column>
      </DataTable>
    </section>
  );
}

export default OrcidReports;
